import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import tzLookup from 'tz-lookup';
import { loadNatalChart, saveNatalChart } from '../lib/chartStorage';
import BirthChartForm, { parseDateInput, parseTimeInput } from '../components/chart/BirthChartForm';
import ChartWheel from '../components/chart/ChartWheel';
import PlacementsTable from '../components/chart/PlacementsTable';
import HousesTable from '../components/chart/HousesTable';
import AspectsTable from '../components/chart/AspectsTable';
import BeginnerSummary from '../components/chart/BeginnerSummary';
import ChartQuickstart from '../components/chart/ChartQuickstart';
import ChartStructurePanel from '../components/chart/ChartStructurePanel';
import InterpretationBridge from '../components/chart/InterpretationBridge';
import SelectionContextPanel from '../components/chart/SelectionContextPanel';
import SelectionFeedbackCard from '../components/chart/SelectionFeedbackCard';
import CTASection from '../components/chart/CTASection';
import { AMM_MAIN_SITE } from '../constants/chartBrandLinks';
import LoadingState from '../components/chart/LoadingState';
import ErrorState from '../components/chart/ErrorState';
import './ChartPage.css';

const PLANET_ORDER = ['sun', 'moon', 'mercury', 'venus', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto', 'northnode', 'lilith', 'chiron'];
const PLANET_LABELS = {
  sun: 'Sun',
  moon: 'Moon',
  mercury: 'Mercury',
  venus: 'Venus',
  mars: 'Mars',
  jupiter: 'Jupiter',
  saturn: 'Saturn',
  uranus: 'Uranus',
  neptune: 'Neptune',
  pluto: 'Pluto',
  northnode: 'North Node',
  lilith: 'Lilith',
  chiron: 'Chiron',
};
const ANGLE_TOOLTIPS = {
  ASC: 'ASC – the sign rising on the eastern horizon at birth.',
  MC: 'MC – the highest point of the chart, linked to direction and public life.',
  DSC: 'DSC – the point opposite the Ascendant, describing how we meet others.',
  IC: 'IC – the lowest point of the chart, linked to roots, home, and foundations.',
};
const SIGN_ORDER = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

const ChartPage = () => {
  const [formValues, setFormValues] = useState({
    birthDate: '',
    birthTime: '',
    timeUnknown: false,
    country: '',
    locationQuery: '',
  });
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [chartResult, setChartResult] = useState(null);

  useEffect(() => {
    const stored = loadNatalChart();
    if (stored?.chartResult) {
      setChartResult(stored.chartResult);
      const firstPlanet = stored.chartResult.chartModel?.planets?.[0];
      if (firstPlanet) {
        setSelectedPlacementKey(firstPlanet.key);
        setSelectedHouse(firstPlanet.house ?? null);
      }
      if (stored.meta) {
        setFormValues((prev) => ({
          ...prev,
          birthDate: stored.meta.birthDate ?? prev.birthDate,
          birthTime: stored.meta.birthTime ?? prev.birthTime,
          timeUnknown: stored.meta.birthTime === 'Time unknown' || prev.timeUnknown,
          locationQuery: stored.meta.location ?? prev.locationQuery,
        }));
      }
    }
  }, []);
  const [selectedPlacementKey, setSelectedPlacementKey] = useState('');
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [selectedAspectId, setSelectedAspectId] = useState(null);
  const [selectedAngleKey, setSelectedAngleKey] = useState('');
  const [hoveredSector, setHoveredSector] = useState(null);

  const onFieldChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    if (key !== 'country' && key !== 'locationQuery') {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
    if (key === 'locationQuery' && selectedLocation && value !== selectedLocation.displayName) {
      setSelectedLocation(null);
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    const parsedDate = parseDateInput(formValues.birthDate);
    const parsedTime = formValues.timeUnknown ? '12:00' : parseTimeInput(formValues.birthTime);

    if (!formValues.birthDate) nextErrors.birthDate = 'Birth date is required.';
    else if (!parsedDate) nextErrors.birthDate = 'Use format dd/mm/yyyy (e.g. 17/10/1977).';
    if (!formValues.timeUnknown && !formValues.birthTime) nextErrors.birthTime = 'Birth time is required unless time is unknown.';
    else if (!formValues.timeUnknown && !parsedTime) nextErrors.birthTime = 'Use format hh:mm (e.g. 20:30).';
    if (!selectedLocation) nextErrors.location = 'Choose a location from search results.';

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (!selectedLocation || !parsedDate || (!formValues.timeUnknown && !parsedTime)) return;
    setLoading(true);
    setError('');

    try {
      const timeToUse = formValues.timeUnknown ? '12:00' : parsedTime;
      const timezone = tzLookup(Number(selectedLocation.lat), Number(selectedLocation.lng));
      const response = await fetch('/api/chart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          birth_date: parsedDate,
          birth_time: timeToUse,
          timezone,
          lat: Number(selectedLocation.lat),
          lng: Number(selectedLocation.lng),
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || !data.ok) {
        throw new Error(data.details || data.message || data.error || 'Chart calculation failed.');
      }

      const normalized = normalizeChartData(data, {
        birthDate: parsedDate,
        birthTime: timeToUse,
        timezone,
        timeUnknown: formValues.timeUnknown,
      });
      setChartResult(normalized);
      setSelectedPlacementKey(normalized.chartModel?.planets?.[0]?.key || '');
      setSelectedHouse(normalized.chartModel?.planets?.[0]?.house || null);
      setSelectedAspectId(null);
      setSelectedAngleKey('');
      saveNatalChart({
        chartResult: normalized,
        meta: {
          birthDate: formValues.birthDate,
          birthTime: formValues.timeUnknown ? 'Time unknown' : formValues.birthTime,
          location: selectedLocation?.displayName || '',
        },
      });
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[chart/generate] failed', err);
      }
      setError('We couldn\'t generate the chart right now. Please try again in a moment.');
      setChartResult(null);
      setSelectedAngleKey('');
    } finally {
      setLoading(false);
    }
  };

  const beginnerSummary = useMemo(() => {
    if (!chartResult) return null;
    const findByKey = (key) => chartResult.placements.find((item) => item.key === key);
    return {
      sun: findByKey('sun'),
      moon: findByKey('moon'),
      ascendant: chartResult.angles.asc,
    };
  }, [chartResult]);

  const placementLookup = useMemo(
    () => new Map((chartResult?.placements || []).map((item) => [item.key, item])),
    [chartResult],
  );

  const handleSelectPlanet = (planetKey) => {
    setSelectedPlacementKey(planetKey ?? null);
    setSelectedAspectId(null);
    setSelectedAngleKey('');
    if (!planetKey) {
      setSelectedHouse(null);
      return;
    }
    const placement = placementLookup.get(planetKey);
    setSelectedHouse(Number.isFinite(Number(placement?.house)) ? Number(placement.house) : null);
  };

  const handleSelectHouse = (houseNumber) => {
    if (houseNumber == null) {
      setSelectedHouse(null);
      setSelectedPlacementKey('');
      return;
    }
    const house = Number(houseNumber);
    setSelectedHouse(house);
    setSelectedAspectId(null);
    setSelectedAngleKey('');
    const match = (chartResult?.placements || []).find((item) => Number(item.house) === house);
    if (match) setSelectedPlacementKey(match.key);
  };

  const handleSelectAspect = (aspectId) => {
    setSelectedAspectId(aspectId);
    setSelectedAngleKey('');
    const aspect = (chartResult?.chartModel?.aspects || []).find((item) => item.id === aspectId);
    if (!aspect) return;
    setSelectedPlacementKey(aspect.p1);
    const planet = (chartResult?.placements || []).find((item) => item.key === aspect.p1);
    setSelectedHouse(Number.isFinite(Number(planet?.house)) ? Number(planet.house) : null);
  };

  const handleSelectAngle = (angleKey) => {
    setSelectedAngleKey(angleKey);
    setSelectedAspectId(null);
    const angleToHouse = { ASC: 1, IC: 4, DSC: 7, MC: 10 };
    const mappedHouse = angleToHouse[angleKey];
    if (Number.isFinite(mappedHouse)) {
      setSelectedHouse(mappedHouse);
      const match = (chartResult?.placements || []).find((item) => Number(item.house) === mappedHouse);
      if (match) setSelectedPlacementKey(match.key);
    }
  };

  const quickstart = useMemo(() => {
    if (!chartResult?.chartModel) return null;
    const placements = chartResult.chartModel.planets;
    const aspects = [...(chartResult.aspects || [])].sort((a, b) => Number(a.orb) - Number(b.orb)).slice(0, 4);
    const get = (key) => placements.find((item) => item.key === key);
    const sun = get('sun');
    const moon = get('moon');
    const rising = chartResult.angles.asc;

    const signCounts = placements.reduce((acc, p) => {
      acc[p.sign] = (acc[p.sign] || 0) + 1;
      return acc;
    }, {});
    const houseCounts = placements.reduce((acc, p) => {
      const h = Number(p.house);
      if (Number.isFinite(h)) acc[h] = (acc[h] || 0) + 1;
      return acc;
    }, {});

    const dominantSign = Object.entries(signCounts).sort((a, b) => b[1] - a[1])[0];
    const dominantHouse = Object.entries(houseCounts).sort((a, b) => b[1] - a[1])[0];
    const dominantSignName = dominantSign?.[0] || '—';
    const dominantHouseNum = dominantHouse ? Number(dominantHouse[0]) : null;
    const dominantSignIndex = SIGN_ORDER.indexOf(dominantSignName);
    const strongestAspects = aspects.length
      ? aspects.map((a) => `${a.p1Label} ${capitalize(a.type)} ${a.p2Label} (${Number(a.orb).toFixed(1)}°)`)
      : ['No major aspects available in this chart.'];

    const overview = `This chart places clear emphasis on ${dominantSignName}${dominantHouseNum ? ` and the ${ordinalHouse(dominantHouseNum)} house` : ''}. That may draw attention toward ${houseTheme(dominantHouseNum)}. ${rising?.sign || 'Rising'} rising shapes first responses, while the ${moon?.sign || 'Moon'} Moon adds emotional pacing to daily decisions.`;

    return {
      bigThree: `${sun?.sign || '—'} Sun · ${moon?.sign || '—'} Moon · ${rising?.sign || '—'} Rising`,
      mainEmphasis: dominantHouseNum
        ? `${dominantSignName} repeats most, with concentration in House ${dominantHouseNum}.`
        : `${dominantSignName} appears as the strongest repeating sign pattern.`,
      angles: `ASC ${chartResult.angles.asc.sign} ${chartResult.angles.asc.degreeText} and MC ${chartResult.angles.mc.sign} ${chartResult.angles.mc.degreeText} orient how life is approached and where direction is built.`,
      strongestAspects,
      overview,
      dominantHouse: dominantHouseNum,
      dominantSignIndex: dominantSignIndex >= 0 ? dominantSignIndex : null,
    };
  }, [chartResult]);

  useEffect(() => {
    if (!chartResult?.chartModel?.planets?.length) return;
    if (selectedPlacementKey && !chartResult.chartModel.planets.some((planet) => planet.key === selectedPlacementKey)) {
      setSelectedPlacementKey(chartResult.chartModel.planets[0].key);
    }
    if (selectedHouse != null && !Number.isFinite(Number(selectedHouse))) {
      setSelectedHouse(Number(chartResult.chartModel.planets[0].house) || null);
    }
    if (selectedAspectId && !chartResult.chartModel.aspects.some((aspect) => aspect.id === selectedAspectId)) {
      setSelectedAspectId(null);
    }
  }, [chartResult, selectedPlacementKey, selectedHouse, selectedAspectId]);

  return (
    <div className="chart-page">
      <header className="amm-header">
        <a href={AMM_MAIN_SITE} aria-label="Astral Moon Magic">
          <img
            src="https://d1yei2z3i6k35z.cloudfront.net/15470577/6994a06c0a4b8_astral-moon-magic-logo-vector.png"
            alt="Astral Moon Magic"
          />
        </a>
      </header>

      <main className="chart-shell">
        <section className="card chart-hero">
          <h1>Generate Your Birth Chart</h1>
          <p>
            This creates the full natal chart used across Astral Moon Magic lessons. You do not need to know astrology yet.
          </p>
        </section>

        <BirthChartForm
          values={formValues}
          errors={errors}
          onChange={onFieldChange}
          onSubmit={onSubmit}
          generating={loading}
          selectedLocation={selectedLocation}
          onLocationSelect={setSelectedLocation}
        />

        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {chartResult && !loading && (
          <>
            <section className="card">
              <div className="chart-meta-row">
                <p><strong>Timezone:</strong> {chartResult.timezone} ({chartResult.timezoneOffset})</p>
                <button
                  type="button"
                  title={ANGLE_TOOLTIPS.ASC}
                  aria-label={ANGLE_TOOLTIPS.ASC}
                  className={`chart-angle-chip${selectedAngleKey === 'ASC' ? ' chart-angle-chip-selected' : ''}`}
                  onClick={() => handleSelectAngle('ASC')}
                >
                  <strong>ASC:</strong> {chartResult.angles.asc.sign} {chartResult.angles.asc.degreeText}
                </button>
                <button
                  type="button"
                  title={ANGLE_TOOLTIPS.MC}
                  aria-label={ANGLE_TOOLTIPS.MC}
                  className={`chart-angle-chip${selectedAngleKey === 'MC' ? ' chart-angle-chip-selected' : ''}`}
                  onClick={() => handleSelectAngle('MC')}
                >
                  <strong>MC:</strong> {chartResult.angles.mc.sign} {chartResult.angles.mc.degreeText}
                </button>
                <button
                  type="button"
                  title={ANGLE_TOOLTIPS.DSC}
                  aria-label={ANGLE_TOOLTIPS.DSC}
                  className={`chart-angle-chip${selectedAngleKey === 'DSC' ? ' chart-angle-chip-selected' : ''}`}
                  onClick={() => handleSelectAngle('DSC')}
                >
                  <strong>DSC:</strong> {chartResult.angles.dsc.sign} {chartResult.angles.dsc.degreeText}
                </button>
                <button
                  type="button"
                  title={ANGLE_TOOLTIPS.IC}
                  aria-label={ANGLE_TOOLTIPS.IC}
                  className={`chart-angle-chip${selectedAngleKey === 'IC' ? ' chart-angle-chip-selected' : ''}`}
                  onClick={() => handleSelectAngle('IC')}
                >
                  <strong>IC:</strong> {chartResult.angles.ic.sign} {chartResult.angles.ic.degreeText}
                </button>
              </div>
              <p className="chart-interaction-hint">Select an angle to highlight its axis on the wheel and show context.</p>
              {selectedAngleKey && chartResult?.chartModel && (
                <SelectionFeedbackCard
                  chart={chartResult.chartModel}
                  selectedAngleKey={selectedAngleKey}
                />
              )}
            </section>

            {chartResult.timeUnknown && (
              <section className="card">
                <p className="chart-help">
                  Time unknown mode is active. Houses, Ascendant, and exact Moon position are approximate.
                </p>
              </section>
            )}

            <ChartQuickstart quickstart={quickstart} />
            <div className="chart-report-entry">
              <Link to="/chart/report" className="chart-report-link">Open printable report</Link>
            </div>
            <ChartStructurePanel
              chart={chartResult.chartModel}
              onHoverSector={(type, value) => setHoveredSector(type && value != null ? { type, value } : null)}
            />
            <ChartWheel
              chart={chartResult.wheelData}
              selectedKey={selectedPlacementKey}
              selectedHouse={selectedHouse}
              selectedAspectId={selectedAspectId}
              selectedAngleKey={selectedAngleKey}
              dominantHouse={quickstart?.dominantHouse}
              dominantSignIndex={quickstart?.dominantSignIndex}
              hoveredSector={hoveredSector}
            />
            <SelectionContextPanel
              chart={chartResult.chartModel}
              selectedPlanetKey={selectedPlacementKey}
              selectedHouse={selectedHouse}
              selectedAspectId={selectedAspectId}
              selectedAngleKey={selectedAngleKey}
            />
            <BeginnerSummary summary={beginnerSummary} />
            <div>
              <PlacementsTable
                placements={chartResult.placements}
                selectedKey={selectedPlacementKey}
                onSelect={handleSelectPlanet}
                chart={chartResult.chartModel}
              />
            </div>
            <div>
              <HousesTable
                houses={chartResult.houses}
                selectedHouse={selectedHouse}
                onSelectHouse={handleSelectHouse}
                chart={chartResult.chartModel}
              />
            </div>
            <div>
              <AspectsTable
                aspects={chartResult.aspects}
                selectedAspectId={selectedAspectId}
                onSelectAspect={handleSelectAspect}
                chart={chartResult.chartModel}
              />
            </div>
            <InterpretationBridge
              chart={chartResult.chartModel}
              selectedPlacementKey={selectedPlacementKey}
              onSelectPlacement={handleSelectPlanet}
            />
            <CTASection />
          </>
        )}
      </main>
    </div>
  );
};

function normalizeChartData(apiData, meta) {
  const planets = Array.isArray(apiData.planets) ? apiData.planets : [];
  const houses = Array.isArray(apiData.houses) ? apiData.houses : [];
  const aspects = Array.isArray(apiData.aspects) ? apiData.aspects : [];
  const angles = apiData.angles || {};

  const housesSorted = [...houses].sort((a, b) => Number(a.house) - Number(b.house));
  const placementRows = planets
    .filter((planet) => PLANET_ORDER.includes(String(planet.key).toLowerCase()))
    .map((planet) => {
      const lon = Number(planet.longitude ?? planet.lon ?? 0);
      const signData = longitudeToSignDegree(lon);
      const derivedHouse = findHouseForLongitude(lon, housesSorted);
      const houseFromApi = Number(planet.house);
      return {
        key: String(planet.key).toLowerCase(),
        body: PLANET_LABELS[String(planet.key).toLowerCase()] || String(planet.key),
        sign: planet.sign || signData.sign,
        degreeText: `${signData.deg}°${String(signData.min).padStart(2, '0')}′`,
        retrograde: Boolean(planet.retrograde || planet.retro),
        house: Number.isFinite(houseFromApi) ? houseFromApi : derivedHouse,
        lon,
      };
    })
    .sort((a, b) => {
      const ai = PLANET_ORDER.indexOf(a.key);
      const bi = PLANET_ORDER.indexOf(b.key);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

  const houseRows = housesSorted.map((house) => {
    const lon = Number(house.lon ?? house.longitude ?? 0);
    const signData = longitudeToSignDegree(lon);
    return {
      house: Number(house.house),
      sign: house.sign || signData.sign,
      degreeText: `${signData.deg}°${String(signData.min).padStart(2, '0')}′`,
      lon,
    };
  });

  const angleData = {
    asc: buildAngleFromLon(angles.ascLon ?? angles.asc?.lon),
    mc: buildAngleFromLon(angles.mcLon ?? angles.mc?.lon),
    dsc: buildAngleFromLon(angles.dcLon),
    ic: buildAngleFromLon(angles.icLon),
  };

  const timezoneOffset = DateTime.fromISO(`${meta.birthDate}T${meta.birthTime}`, { zone: meta.timezone }).toFormat("'UTC'ZZ");

  const chartModel = {
    planets: placementRows.map((row) => ({
      key: row.key,
      name: row.body,
      sign: row.sign,
      degreeText: row.degreeText,
      house: row.house,
      lon: row.lon,
    })),
    houses: houseRows.map((row) => ({
      house: row.house,
      sign: row.sign,
      degreeText: row.degreeText,
      lon: row.lon,
    })),
    angles: angleData,
    aspects: aspects.map((aspect) => ({
      id: `${String(aspect.p1 || aspect.aKey || '').toLowerCase()}-${String(aspect.p2 || aspect.bKey || '').toLowerCase()}-${String(aspect.type || aspect.aspectKey || 'conjunction')}`,
      p1: String(aspect.p1 || aspect.aKey || '').toLowerCase(),
      p2: String(aspect.p2 || aspect.bKey || '').toLowerCase(),
      type: aspect.type || aspect.aspectKey || 'conjunction',
      orb: Number(aspect.orb || 0),
    })),
  };

  return {
    timezone: meta.timezone,
    timezoneOffset,
    timeUnknown: meta.timeUnknown,
    placements: placementRows,
    houses: houseRows,
    aspects: aspects.map((aspect) => ({
      id: `${String(aspect.p1 || aspect.aKey || '').toLowerCase()}-${String(aspect.p2 || aspect.bKey || '').toLowerCase()}-${String(aspect.type || aspect.aspectKey || 'conjunction')}`,
      p1: aspect.p1 || aspect.aKey || '',
      p2: aspect.p2 || aspect.bKey || '',
      p1Label: PLANET_LABELS[String(aspect.p1 || aspect.aKey || '').toLowerCase()] || String(aspect.p1 || aspect.aKey || '').toUpperCase(),
      p2Label: PLANET_LABELS[String(aspect.p2 || aspect.bKey || '').toLowerCase()] || String(aspect.p2 || aspect.bKey || '').toUpperCase(),
      type: aspect.type || aspect.aspectKey || 'conjunction',
      orb: Number(aspect.orb || 0),
    })),
    angles: angleData,
    chartModel,
    wheelData: {
      meta: apiData.meta || {},
      planets: chartModel.planets.map((planet) => ({
        key: planet.key,
        nameFi: planet.name,
        lon: planet.lon,
        sign: planet.sign,
        house: planet.house,
      })),
      houses: chartModel.houses.map((house) => ({
        house: house.house,
        lon: house.lon,
        sign: house.sign,
      })),
      aspects: chartModel.aspects,
      angles: {
        ascLon: Number(chartModel.angles.asc.lon ?? 0),
        mcLon: Number(chartModel.angles.mc.lon ?? 0),
        dcLon: Number(chartModel.angles.dsc.lon ?? 0),
        icLon: Number(chartModel.angles.ic.lon ?? 0),
      },
    },
  };
}

function longitudeToSignDegree(longitude) {
  const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
  const normalized = ((Number(longitude) % 360) + 360) % 360;
  const signIndex = Math.floor(normalized / 30);
  const inSign = normalized % 30;
  const deg = Math.floor(inSign);
  const min = Math.round((inSign - deg) * 60);
  return { sign: signs[signIndex], deg, min };
}

function buildAngleFromLon(longitude) {
  const value = Number(longitude || 0);
  const signData = longitudeToSignDegree(value);
  return {
    sign: signData.sign,
    degreeText: `${signData.deg}°${String(signData.min).padStart(2, '0')}′`,
    lon: value,
  };
}

function findHouseForLongitude(longitude, houses) {
  if (!Array.isArray(houses) || houses.length !== 12) return null;
  const target = ((Number(longitude) % 360) + 360) % 360;
  for (let i = 0; i < houses.length; i++) {
    const start = ((Number(houses[i].lon) % 360) + 360) % 360;
    const end = ((Number(houses[(i + 1) % houses.length].lon) % 360) + 360) % 360;
    if (start <= end) {
      if (target >= start && target < end) return houses[i].house;
    } else if (target >= start || target < end) {
      return houses[i].house;
    }
  }
  return 1;
}

function capitalize(value) {
  if (!value) return value;
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

function ordinalHouse(value) {
  const house = Number(value);
  if (house === 1) return '1st';
  if (house === 2) return '2nd';
  if (house === 3) return '3rd';
  return `${house}th`;
}

function houseTheme(house) {
  const themes = {
    1: 'identity, presence, and first responses',
    2: 'resources, security, and personal value',
    3: 'communication patterns and local rhythms',
    4: 'home, roots, and emotional foundations',
    5: 'creativity, visibility, and self-expression',
    6: 'work habits, health routines, and craft',
    7: 'partnership and relational contracts',
    8: 'shared bonds, trust, and depth work',
    9: 'belief, learning, and perspective building',
    10: 'vocation, direction, and public contribution',
    11: 'community ties and future orientation',
    12: 'private processing, retreat, and closure cycles',
  };
  return themes[Number(house)] || 'the strongest repeating life themes in the chart';
}

export default ChartPage;

async function parseJsonResponse(response) {
  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  if (!contentType.includes('application/json')) {
    const snippet = (await response.text()).slice(0, 200);
    throw new Error(`non_json_response:${response.status}:${snippet}`);
  }
  return response.json();
}
